import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { downloadFileStreaming } from "utilities/download-helper";
import { useDownloadFileMutation } from "redux/reducers/filesApi";
import { getBucketNameFromFileName, ENV_FILE_PREFIX } from "@ubiops/widgets";
import { LOADING, LOADED, ERROR } from "utilities/request-statuses";
import { createErrorNotification } from "utilities/notifications";

const useDownloadFile = (token) => {
  const { projectName } = useParams();
  const dispatch = useDispatch();
  const [download] = useDownloadFileMutation();
  const [downloadFileStatus, setDownloadFileStatus] = useState({});
  const [filePreviews, setFilePreviews] = useState({});

  const downloadFile = useCallback(async (fileName, preview = false) => {
    if (!fileName) return;
    setDownloadFileStatus((prev) => ({ ...prev, [fileName]: LOADING }));

    const bucketName = getBucketNameFromFileName(fileName);
    const bucketPrefix = `${ENV_FILE_PREFIX}${bucketName}/`;
    const { data, error } = await download({
      projectName,
      bucketName,
      fileName: fileName.replace(bucketPrefix, ""),
      token,
    });

    if (error) {
      setDownloadFileStatus((prev) => ({ ...prev, [fileName]: ERROR }));
      const errorMessage = error?.data?.error || "Failed to download file. You may not have permission.";
      dispatch(createErrorNotification(errorMessage));
    } else {
      if (preview) {
        const filename = fileName.split("/").pop();
        setFilePreviews((prev) => ({ ...prev, [fileName]: { objectUrl: data?.url, filename } }));
      } else {
        data?.url && downloadFileStreaming(data.url);
      }
      setDownloadFileStatus((prev) => ({ ...prev, [fileName]: LOADED }));
    }
  }, [dispatch, download, projectName, token]);

  const downloadFileReset = useCallback((fileName) => {
    if (fileName) {
      setDownloadFileStatus((prev) => { const next = { ...prev }; delete next[fileName]; return next; });
      setFilePreviews((prev) => { const next = { ...prev }; delete next[fileName]; return next; });
    } else {
      setDownloadFileStatus({});
      setFilePreviews({});
    }
  }, []);

  return { downloadFile, downloadFileStatus, downloadFileReset, filePreviews };
};

export default useDownloadFile;
